import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "../api/axiosInstance";
import { isDemoMode } from "../demo/demoMode";
import { persistAuthSession, readStoredAuthToken, readStoredUser } from "../utils/sessionUser";
import { usePmsTheme } from "./PmsThemeContext";

const sessionKey = (token, userId) => JSON.stringify([token, String(userId || "")]);
const storedSessionKey = () => sessionKey(readStoredAuthToken(), readStoredUser()?.id);

// Workspace choices and corporate entry must not render cached grants before
// the server has checked the current roles. API authorization remains server-side.
export default function SessionAccessGate({ children }) {
  const { languageId } = usePmsTheme();
  const spanish = /^es(?:-|$)/i.test(languageId || "");
  const demo = isDemoMode();
  const [currentKey, setCurrentKey] = useState(storedSessionKey);
  const [verification, setVerification] = useState(null);
  const [retry, setRetry] = useState(0);
  const verifiedKey = useRef(null);
  const [token, userId] = JSON.parse(currentKey);

  useEffect(() => {
    const sync = () => setCurrentKey(storedSessionKey());
    window.addEventListener("palantir-session-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("palantir-session-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    if (demo || !token || !userId || verifiedKey.current === currentKey) return undefined;
    const controller = new AbortController();
    setVerification(null);
    const isCurrentSession = () => {
      if (controller.signal.aborted) return false;
      const latestKey = storedSessionKey();
      if (latestKey === currentKey) return true;
      // A login, logout or BU switch won the race: never restore the old session.
      setCurrentKey(latestKey);
      return false;
    };
    axios.get("/api/auth/session", { signal: controller.signal, timeout: 15_000 })
      .then(({ data }) => {
        if (!isCurrentSession()) return;
        if (typeof data?.token !== "string" || !data.token.trim() || String(data?.user?.id || "") !== userId
          || !Array.isArray(data?.user?.corporate_modules)) {
          throw new Error("Invalid session access response");
        }
        const refreshedKey = sessionKey(String(data.token).trim(), data.user.id);
        // persistAuthSession emits a session event. Accept our own refreshed
        // token without starting a second request, but recheck external tokens.
        verifiedKey.current = refreshedKey;
        persistAuthSession(data);
        setCurrentKey(refreshedKey);
        setVerification({ key: refreshedKey, failed: false });
      })
      .catch(() => {
        if (isCurrentSession()) setVerification({ key: currentKey, failed: true });
      });
    return () => controller.abort();
  }, [currentKey, demo, retry, token, userId]);

  if (!token || !userId) return <Navigate to="/" replace />;
  if (demo || (verification?.key === currentKey && !verification.failed)) return children;
  const failed = verification?.key === currentKey && verification.failed;
  return (
    <main className="p-4 text-center">
      <p role={failed ? "alert" : "status"}>
        {failed
          ? (spanish ? "No se han podido comprobar tus permisos. Vuelve a intentarlo."
            : "Could not check your access. Please try again.")
          : (spanish ? "Comprobando permisos…" : "Checking access…")}
      </p>
      {failed && <button type="button" className="btn btn-outline-primary" onClick={() => setRetry((value) => value + 1)}>
        {spanish ? "Reintentar" : "Try again"}
      </button>}
    </main>
  );
}
